// Multilingual support for form fields.
//
// Each field keeps its canonical (English/default) configuration on the
// top-level keys it always had (`label`, `placeholder`, ...). Translations for
// other languages live in an optional `translations` map keyed by language
// code, e.g.
//
//   {
//     id: "abc",
//     formType: "dropdownField",
//     label: "Status",                 // default (English)
//     placeholder: "Select status",
//     mandatory: true,                 // shared across languages
//     options: [
//       { label: "Open", value: "v1" },
//       { label: "Closed", value: "v2" },
//     ],
//     translations: {
//       japanese: {
//         label: "ステータス",
//         placeholder: "ステータスを選択",
//         // List translations mirror the default list shape: an array of items
//         // carrying their stable id (option `value`, column `id`) plus the
//         // translated text. Matching by id means translations survive
//         // reordering; table columns nest their own translated `options`.
//         options: [
//           { value: "v1", label: "未対応" },
//           { value: "v2", label: "完了" },
//         ],
//       },
//     },
//   }
//
// Only user-facing text is translated; structural/validation settings are
// shared. The model is backward compatible: forms saved before this feature
// simply have no `translations` key and resolve to their default values.

export const DEFAULT_LANGUAGE = "english";

// App-wide supported languages. Locale codes match /public/locales so the
// i18next `lng`, user `defaultLanguage`, and translation buckets all share
// the same codes. Import this list for every language dropdown.
export const FORM_LANGUAGES = [
    { code: "english", label: "English" },
    { code: "japanese", label: "日本語 (Japanese)" },
    { code: "chinese", label: "简体中文 (Simplified Chinese)" },
    { code: "traditionalChinese", label: "繁體中文 (Traditional Chinese)" },
    { code: "korean", label: "한국어 (Korean)" },
];

export const FORM_LANGUAGE_OPTIONS = FORM_LANGUAGES.map((lang) => ({
    value: lang.code,
    label: lang.label,
}));

// Scalar string attributes that hold user-facing text. Everything not listed
// here (switches, numbers, sort order, ...) is treated as shared configuration.
// `value`/`id`/`inputType`/etc. are intentionally excluded so they stay stable
// across languages.
const LEAF_KEYS = [
    "label",
    "placeholder",
    "helpText",
    "description",
    "formInfo",
    "infoDescription",
];

// Array attributes whose item objects carry translatable text. Each entry maps
// to a config:
//   idKey   - stable property used to match an item across languages (`null`
//             falls back to the array index).
//   textKey - when set, the item carries a single translatable string at this
//             property; the stored translation keeps the same array-of-items
//             shape as the default list (`[{ value, label }, ...]`). When
//             `null`, the item is processed recursively with this same config,
//             so a table column's nested `options` are picked up automatically.
const LIST_KEYS = {
    options: { idKey: "value", textKey: "label" },
    infoLinks: { idKey: null, textKey: "label" },
    columns: { idKey: "id", textKey: null },
};

// Back-compat export: the flat set of translatable scalar keys.
export const TRANSLATABLE_FIELD_KEYS = LEAF_KEYS;

const isPlainObject = (value) =>
    value !== null && typeof value === "object" && !Array.isArray(value);

// Ant Design Form.List `onValuesChange` reports sparse edits as an object
// (`{ 0: { label } }`) rather than an array. Normalize so extract/merge can
// treat columns and options the same way getFieldsValue(true) does.
const asItemList = (value) => {
    if (Array.isArray(value)) return value;
    if (!isPlainObject(value)) return null;
    const keys = Object.keys(value)
        .filter((key) => /^\d+$/.test(key))
        .sort((a, b) => Number(a) - Number(b));
    if (keys.length === 0) return null;
    return keys.map((key) => value[key]);
};

const listItemId = (item, idKey) => {
    if (!item || !idKey) return undefined;
    if (item[idKey] !== undefined && item[idKey] !== null) return item[idKey];
    // Table columns have been stored as `id` and, in older payloads, `_id`.
    if (idKey === "id" && item._id !== undefined && item._id !== null) return item._id;
    return undefined;
};

const itemKey = (item, idKey, index) => {
    const id = listItemId(item, idKey);
    if (id !== undefined) return String(id);
    return String(index);
};

// Read a stored translation for a single-text (`textKey`) list item. Accepts
// the current item-object shape (`{ value, label }`) as well as the legacy flat
// string shape so previously saved translations keep resolving.
const readLeafText = (override, textKey) => {
    if (typeof override === "string") return override;
    if (isPlainObject(override) && typeof override[textKey] === "string") {
        return override[textKey];
    }
    return undefined;
};

// Locate the stored translation entry for a single source item. Tolerant of the
// shapes translations have been saved in over time:
//   - array of items (current): matched by `idKey`, falling back to index.
//   - object map keyed by id: `{ v1: "未対応" }` or `{ v1: { label: "未対応" } }`.
const findListOverride = (stored, idKey, item, index) => {
    const key = itemKey(item, idKey, index);
    if (Array.isArray(stored)) {
        if (idKey) {
            const match = stored.find(
                (entry) => isPlainObject(entry) && String(listItemId(entry, idKey) ?? "") === key
            );
            if (match) return match;
        }
        return stored[index];
    }
    if (isPlainObject(stored)) return stored[key];
    return undefined;
};

// Pull the translatable text out of an arbitrary values/field object into a
// language-agnostic shape: leaf strings stay as-is and lists mirror the default
// list shape — an array of items carrying their stable id plus translatable
// text. Empty strings are kept so an author can clear a translation.
export const extractTranslatableValues = (source = {}) => {
    const result = {};
    if (!isPlainObject(source)) return result;

    for (const key of LEAF_KEYS) {
        if (typeof source[key] === "string") {
            result[key] = source[key];
        }
    }

    for (const [key, { idKey, textKey }] of Object.entries(LIST_KEYS)) {
        const list = asItemList(source[key]);
        if (!list || list.length === 0) continue;
        result[key] = list.map((item) => {
            if (!isPlainObject(item)) return {};
            const entry = {};
            const id = listItemId(item, idKey);
            if (id !== undefined) {
                entry[idKey] = id;
            }
            if (textKey) {
                entry[textKey] = typeof item[textKey] === "string" ? item[textKey] : "";
            } else {
                Object.assign(entry, extractTranslatableValues(item));
            }
            return entry;
        });
    }

    return result;
};

// Back-compat alias used by older call sites.
export const pickTranslatableValues = extractTranslatableValues;

// Merge stored `overrides` onto a field for rendering. Missing or empty
// translations fall back to the default value. Returns the original reference
// when nothing changed so renderers can rely on referential stability.
const applyTranslations = (field, overrides) => {
    if (!isPlainObject(field) || !isPlainObject(overrides)) return field;

    let result = field;
    const ensureCopy = () => {
        if (result === field) result = { ...field };
    };

    for (const key of LEAF_KEYS) {
        const value = overrides[key];
        if (typeof value === "string" && value !== "") {
            ensureCopy();
            result[key] = value;
        }
    }

    for (const [key, { idKey, textKey }] of Object.entries(LIST_KEYS)) {
        const stored = overrides[key];
        if (!Array.isArray(field[key])) continue;
        if (!Array.isArray(stored) && !isPlainObject(stored)) continue;
        let newArr = field[key];
        let arrCopied = false;
        const copyArr = () => {
            if (!arrCopied) {
                newArr = [...field[key]];
                arrCopied = true;
            }
        };
        field[key].forEach((item, index) => {
            if (!isPlainObject(item)) return;
            const childOverride = findListOverride(stored, idKey, item, index);
            if (childOverride === undefined) return;
            if (textKey) {
                const text = readLeafText(childOverride, textKey);
                if (typeof text === "string" && text !== "" && item[textKey] !== text) {
                    copyArr();
                    newArr[index] = { ...item, [textKey]: text };
                }
                return;
            }
            if (!isPlainObject(childOverride)) return;
            const mergedItem = applyTranslations(item, childOverride);
            if (mergedItem !== item) {
                copyArr();
                newArr[index] = mergedItem;
            }
        });
        if (arrCopied) {
            ensureCopy();
            result[key] = newArr;
        }
    }

    return result;
};

const findColumnOverride = (stored, col, index) => {
    if (!Array.isArray(stored) && !isPlainObject(stored)) return undefined;
    return findListOverride(stored, "id", col, index);
};

const mergeTranslatedOptions = (options, overrideOptions) => {
    if (!Array.isArray(options) || options.length === 0) return options;
    if (!Array.isArray(overrideOptions) && !isPlainObject(overrideOptions)) return options;
    let changed = false;
    const next = options.map((opt, index) => {
        if (!isPlainObject(opt)) return opt;
        const override = findListOverride(overrideOptions, "value", opt, index);
        const text = readLeafText(override, "label");
        if (typeof text !== "string" || text === "" || opt.label === text) return opt;
        changed = true;
        return { ...opt, label: text };
    });
    return changed ? next : options;
};

// Apply `translations[lng].columns` onto the canonical column list.
// Matched by column id, then by index. Empty labels keep the default text;
// option labels (radio / dropdown / checkbox) are merged the same way.
export const applyColumnListTranslation = (columns = [], translatedColumns) => {
    if (!Array.isArray(columns) || columns.length === 0) return columns;
    if (!Array.isArray(translatedColumns) && !isPlainObject(translatedColumns)) return columns;

    let changed = false;
    const next = columns.map((col, index) => {
        if (!isPlainObject(col)) return col;
        const override = findColumnOverride(translatedColumns, col, index);
        if (!isPlainObject(override)) return col;

        let updated = col;
        const copy = () => {
            if (updated === col) updated = { ...col };
        };

        if (typeof override.label === "string" && override.label !== "" && override.label !== col.label) {
            copy();
            updated.label = override.label;
        }

        if (override.options !== undefined) {
            const mergedOptions = mergeTranslatedOptions(col.options, override.options);
            if (mergedOptions !== col.options) {
                copy();
                updated.options = mergedOptions;
            }
        }

        if (updated !== col) changed = true;
        return updated;
    });
    return changed ? next : columns;
};

// Table headers live on `translations[lng].columns`. Keep the canonical
// column objects (id, inputType, ...) and only replace their labels/options
// from that language list, matching by id then by index.
const overlayTranslatedColumns = (field, overrides) => {
    const nextColumns = applyColumnListTranslation(field?.columns, overrides?.columns);
    if (nextColumns === field?.columns) return field;
    return { ...field, columns: nextColumns };
};

export const resolveFieldTranslation = (field, lng) => {
    if (!field || !lng || lng === DEFAULT_LANGUAGE) return field;
    const overrides = field?.translations?.[lng];
    if (!overrides) return field;
    return overlayTranslatedColumns(applyTranslations(field, overrides), overrides);
};

// Build the values to load into the editor when translating into `lng`: every
// translatable text is replaced by its stored translation, or blanked so the
// author can fill it in. Shared (non-text) settings are kept untouched so the
// widget's config panel still renders and behaves normally.
const applyTranslationDraft = (field, overrides) => {
    if (!isPlainObject(field)) return field;
    const ov = isPlainObject(overrides) ? overrides : {};
    const result = { ...field };

    for (const key of LEAF_KEYS) {
        if (typeof field[key] === "string") {
            const value = ov[key];
            result[key] = typeof value === "string" ? value : "";
        }
    }

    for (const [key, { idKey, textKey }] of Object.entries(LIST_KEYS)) {
        if (!Array.isArray(field[key])) continue;
        const stored = ov[key];
        result[key] = field[key].map((item, index) => {
            if (!isPlainObject(item)) return item;
            const override = findListOverride(stored, idKey, item, index);
            if (textKey) {
                if (typeof item[textKey] !== "string") return item;
                const text = readLeafText(override, textKey);
                return { ...item, [textKey]: typeof text === "string" ? text : "" };
            }
            return applyTranslationDraft(item, isPlainObject(override) ? override : undefined);
        });
    }

    return result;
};

export const buildTranslationDraft = (field, lng) => {
    if (!field || lng === DEFAULT_LANGUAGE) return field;
    const overrides = field?.translations?.[lng] || {};
    return overlayTranslatedColumns(applyTranslationDraft(field, overrides), overrides);
};
